import { LoadingButton } from '@mui/lab';
import { Button, Card, CardContent, Dialog, DialogActions, DialogContent, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useMemo, useState } from 'react';
import { useTranslate } from 'src/locales';
import IdibuAuthSettings from 'src/sections/admin/integrations/providers/idibu/auth-settings';
import DialpadAuthSettings from 'src/sections/admin/integrations/providers/dialpad/auth-settings';
import { useConnectIntegrationMutation } from 'src/services/integration/integrations.service';
import { IntegrationProvider, IntegrationProviderType } from 'src/services/integration/integrations.types';

// ----------------------------------------------------------------------

type Props = {
  open: boolean;
  provider: IntegrationProvider;
  onClose: () => void;
  onSuccess?: () => void;
};

export default function ManualAuthModal({
  open,
  provider,
  onSuccess,
  onClose
}: Props) {

  const { t } = useTranslate();

  const [configState, setConfigState] = useState<any>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [connectIntegration, { isLoading }] = useConnectIntegrationMutation();

  const isManualAuthSupported = useMemo(() => provider?.auth_type === 'manual', [provider]);

  const renderAuthCard = () => {

    switch (provider.type) {
      case IntegrationProviderType.IDIBU:
        return (
          <Stack>
            <IdibuAuthSettings
              onChange={(state) => setConfigState(state)}
            />
          </Stack>
        );
      case IntegrationProviderType.DIALPAD:
        return (
          <Stack>
            <DialpadAuthSettings
              onChange={(state) => setConfigState(state)}
            />
          </Stack>
        );
      default:
        return (
          <Stack>
            <Typography variant="body2" textAlign="center">
              {t('integrations.manual_auth.not_supported', { provider: provider.name })}
            </Typography>
          </Stack>
        )
    };
  }

  const handleManualAuthSetup = useCallback(async () => {
    setErrorMessage(null);

    // validate configState
    switch (provider.type) {
      case IntegrationProviderType.IDIBU:
        if (!configState.app_id || !configState.app_secret) {
          setErrorMessage(t('integrations.manual_auth.idibu.validation_error'));
          return;
        }
        break;
      case IntegrationProviderType.DIALPAD:
        if (!configState.api_key) {
          setErrorMessage(t('integrations.manual_auth.dialpad.validation_error'));
          return;
        }
        break;
      default:
        return;
    }

    try {
      const integrationResponse = await connectIntegration({
        provider: provider.type,
        data: configState
      }).unwrap();

      enqueueSnackbar(t('integrations.api.complete_connection.success'), { variant: 'success' });

      if (onSuccess) {
        onSuccess();
      }
    }
    catch (error) {
      enqueueSnackbar(t('integrations.api.complete_connection.default_error'), { variant: 'error' });
    }

  }, [configState, provider, onSuccess, connectIntegration, t]);

  return (

    <Dialog open={open} maxWidth="sm" fullWidth>

      <DialogContent sx={{ p: 1 }}>
        <Card sx={{ width: '100%', boxShadow: 0 }}>
          <CardContent>
            {renderAuthCard()}

            {
              errorMessage && (
                <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                  {errorMessage}
                </Typography>
              )
            }
          </CardContent>
        </Card>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" onClick={onClose}>
            {t('common.close')}
          </Button>
          <LoadingButton
            variant="contained"
            loading={isLoading}
            onClick={handleManualAuthSetup}
            disabled={!isManualAuthSupported}
          >
            {t('common.setup')}
          </LoadingButton>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}